import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Pagination,
  EffectCoverflow,
  Autoplay,
} from "swiper/modules";
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight, ChevronDown, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";

import PageShell from "@/components/PageShell";
import TraditionTag from "@/components/TraditionTag";
import ScripturalAnchor from "@/components/ScripturalAnchor";
import StorefrontButton, {
  BuyDirectSlot,
} from "@/components/StorefrontButton";
import EmailCapture from "@/components/EmailCapture";
import BookCard from "@/components/BookCard";
import { getBook, getStorefronts, priceLine } from "@/lib/catalog";
import {
  site,
  bookPalettes,
  bookHeroes,
  bookCoverHasText,
  bookCrossSell,
} from "@/data/enrichments";
import { trackEvent, installEngagementTracking, reportBuyNavOutcome } from "@/lib/tracking";
import { addUtm } from "@/lib/utm";
import { detectCountry, selectPrimaryCTA } from "@/lib/geo";
import { shouldRewriteOutbound, buildAndroidIntentUrl } from "@/lib/outbound";

export default function BookDetail() {
  const { slug } = useParams();
  const book = getBook(slug);
  const swiperRef = useRef(null);

  // Geo-aware primary CTA. Depends on slug (stable) — not on book (a fresh
  // object on every render, which would loop the effect indefinitely).
  const [primaryCTA, setPrimaryCTA] = useState(null);
  useEffect(() => {
    if (!book) return;
    let mounted = true;
    detectCountry().then((country) => {
      if (!mounted) return;
      setPrimaryCTA(selectPrimaryCTA(book, country));
    });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Fire view_content + install engagement tracking. Defensive: gate on a
  // ref keyed by slug so the effect can re-run all it wants (StrictMode,
  // Framer remounts, etc.) but view_content only fires once per unique slug.
  // Empirically this useEffect was firing 14+ times/sec on production despite
  // [slug] deps — root cause not yet pinned, ref-guard makes it idempotent.
  const firedFor = useRef(null);
  useEffect(() => {
    if (!book) return;
    if (firedFor.current === slug) return;
    firedFor.current = slug;
    trackEvent("view_content", {
      title_id: book.slug,
      slug: book.slug,
      hero_image: bookHeroes[book.slug] || book.cover_url,
      currency: "USD",
      value: 0,
    });
    return installEngagementTracking({ title_id: book.slug, slug: book.slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!book) {
    return (
      <PageShell title="Not found">
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-3xl text-ink">Book not found</h1>
          <p className="font-serif text-ink-soft mt-3">
            <Link to="/books" className="underline decoration-dharma-gold">
              Back to all books
            </Link>
          </p>
        </div>
      </PageShell>
    );
  }

  const palette = bookPalettes[book.slug] || { mood: "earth-sunlight", accent_hex: "#B8884A" };
  const heroImg = bookHeroes[book.slug] || book.cover_url;
  // If the hero image already has the title baked in (cover-as-hero), skip
  // rendering the overlaid <h1> + scriptural line so we don't double up.
  const heroTextSuppressed = bookCoverHasText.has(book.slug);
  const crossSellSlugs = bookCrossSell[book.slug] || [];
  const crossSellBooks = crossSellSlugs.map((s) => getBook(s)).filter(Boolean);

  const storefronts = getStorefronts(book);
  const primary = storefronts.filter((s) => !s.secondary && !s.comingSoon);
  const secondary = storefronts.filter((s) => s.secondary);  // print editions
  const coming = storefronts.filter((s) => s.comingSoon);

  // For the hero — show every digital storefront in one row. Geo-relevant
  // store first (becomes the visually prominent solid button); the rest
  // follow as ghost pills. Print editions stay below in their own row.
  const orderedStores = (() => {
    const list = [...primary];
    if (primaryCTA?.store) {
      const idx = list.findIndex((s) => s.store === primaryCTA.store);
      if (idx > 0) {
        const [match] = list.splice(idx, 1);
        list.unshift(match);
      }
    }
    return [...list, ...coming];
  })();

  // Apply palette as CSS vars for this page only.
  const paletteStyle = {
    "--book-accent": palette.accent_hex,
  };

  return (
    <PageShell title={book.name} paddingTop={false}>
      <div style={paletteStyle}>
        {/* === HERO (painterly art, primary CTA, scroll cue) === */}
        <section
          className="relative -mt-24 md:-mt-28 min-h-[88vh] md:min-h-[100vh] flex items-end overflow-hidden bg-shrine-shadow"
        >
          <div className="absolute inset-0">
            <img
              src={heroImg}
              alt={book.name}
              className="absolute inset-0 w-full h-full object-cover opacity-95"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(10,8,5,0.30) 0%, rgba(10,8,5,0.10) 35%, rgba(10,8,5,0.65) 78%, rgba(248,240,226,0.96) 100%)",
              }}
            />
          </div>

          <div className="relative z-10 w-full max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pb-24 md:pb-36 pt-28 md:pt-32">
            <div className="flex items-center gap-3 flex-wrap mb-5">
              <TraditionTag tradition={book.tradition} className="!bg-cream/15 !border-cream/30 !text-cream" />
              {book.is_pending && (
                <span className="tradition-tag !bg-saffron-fire/15 !border-saffron-fire/40 !text-saffron-fire">
                  Launching
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 mb-4">
              <img src={site.logo_icon} alt="" className="h-5 w-auto select-none" draggable={false} />
              <span className="font-body text-[0.65rem] tracking-widest2 uppercase" style={{ color: palette.accent_hex }}>
                A DharmaWeave book
              </span>
            </div>

            {!heroTextSuppressed && (
              <>
                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display text-cream text-balance text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tightish max-w-4xl"
                >
                  {book.name}
                </motion.h1>

                {book.anchor?.line && (
                  <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.0, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="font-serif italic text-cream/85 text-lg md:text-2xl mt-6 max-w-2xl leading-relaxed"
                  >
                    {book.anchor.line}
                  </motion.p>
                )}
              </>
            )}

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9"
            >
              <div className="eyebrow text-cream/60 mb-3">Read on</div>
              {/* All storefronts in the hero — no scroll, no indirection.
                  Geo-relevant store renders first + styled solid. */}
              <div className="flex flex-wrap items-center gap-2.5">
                {orderedStores.map((s, i) => {
                  const isFirst = i === 0;
                  if (s.comingSoon || !s.url) {
                    return (
                      <span
                        key={s.id}
                        className="btn-pill !text-cream/55 !border-cream/30 cursor-default"
                        title="Coming soon"
                      >
                        {s.label}
                      </span>
                    );
                  }
                  const stamped = addUtm(s.url, { campaign: `books-${book.slug}`, content: s.store });
                  return (
                    <a
                      key={s.id}
                      href={stamped}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`btn-pill ${isFirst ? "btn-pill-solid" : "!text-cream !border-cream/55 hover:!bg-cream hover:!text-ink"}`}
                      style={isFirst ? { background: palette.accent_hex, borderColor: palette.accent_hex } : undefined}
                      onClick={(e) => {
                        const props = {
                          title_id: book.slug,
                          slug: book.slug,
                          store: s.store,
                          market: s.market,
                          outbound_url: stamped,
                          cta_position: "hero",
                        };
                        trackEvent("buy_click", props);
                        if (shouldRewriteOutbound(s.store)) {
                          e.preventDefault();
                          window.location.href = buildAndroidIntentUrl(stamped, s.store);
                        }
                        reportBuyNavOutcome(props);
                      }}
                    >
                      {s.label}
                      <ExternalLink size={13} />
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </div>

          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-cream/70"
          >
            <ChevronDown size={18} />
          </motion.div>
        </section>

        {/* === Inside the book — moved up so the painterly art is the
            FIRST thing after the hero, before the synopsis. Holds the
            visitor's attention with the actual product. === */}
        {book.sample_panels.length > 0 && (
          <section className="bg-shrine-shadow text-cream section-y">
            <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
              <div className="mb-10">
                <div className="eyebrow text-dawn-rose mb-3">Inside the book</div>
                <h2 className="font-display text-3xl md:text-5xl text-cream tracking-tightish max-w-3xl">
                  A few painterly panels.
                </h2>
              </div>
              <Swiper
                modules={[EffectCoverflow, Pagination, Autoplay]}
                effect="coverflow"
                centeredSlides
                grabCursor
                slidesPerView={1.2}
                breakpoints={{
                  640: { slidesPerView: 1.6 },
                  1024: { slidesPerView: 2.4 },
                }}
                coverflowEffect={{
                  rotate: 0,
                  stretch: 0,
                  depth: 140,
                  modifier: 1.6,
                  slideShadows: false,
                }}
                autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
                loop
                pagination={{ clickable: true }}
                speed={900}
                ref={swiperRef}
              >
                {book.sample_panels.map((p, i) => (
                  <SwiperSlide key={i}>
                    <button
                      type="button"
                      onClick={() =>
                        trackEvent("view_sample_panel", {
                          title_id: book.slug,
                          slug: book.slug,
                          panel_index: i,
                          interaction: "click",
                        })
                      }
                      className="block w-full"
                    >
                      <div
                        className={`${
                          p.aspect === "square" ? "aspect-square" :
                          p.aspect === "landscape" ? "aspect-[3/2]" :
                          "aspect-[3/4]"
                        } rounded-sm overflow-hidden border border-cream/15`}
                      >
                        <img src={p.url} alt={p.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="mt-4 text-left">
                        <p className="font-display text-sm md:text-base text-cream tracking-widest2">{p.title}</p>
                        <p className="font-serif italic text-cream/70 text-sm mt-1">{p.caption}</p>
                      </div>
                    </button>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="flex justify-center gap-3 mt-10">
                <button
                  onClick={() => swiperRef.current?.swiper.slidePrev()}
                  className="w-11 h-11 rounded-full border border-cream/25 text-cream hover:bg-cream hover:text-ink transition-all duration-700 ease-breath flex items-center justify-center"
                  aria-label="Previous"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => swiperRef.current?.swiper.slideNext()}
                  className="w-11 h-11 rounded-full border border-cream/25 text-cream hover:bg-cream hover:text-ink transition-all duration-700 ease-breath flex items-center justify-center"
                  aria-label="Next"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* === Back link === */}
        <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pt-8">
          <Link
            to="/books"
            className="inline-flex items-center gap-2 font-body text-xs tracking-widest2 uppercase text-ink-mute hover:text-dharma-gold-deep transition-colors"
          >
            <ArrowLeft size={14} />
            All books
          </Link>
        </div>

        {/* === Synopsis + cover + print editions === */}
        <section className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14 pt-10 md:pt-14 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 lg:sticky lg:top-28"
            >
              <div className="card-shrine rounded-sm overflow-hidden aspect-[2/3] bg-parchment-deep">
                <img src={book.cover_url} alt={book.name} className="w-full h-full object-cover" />
              </div>
              {priceLine(book) && (
                <p className="font-body text-xs text-ink-mute mt-4 text-center tracking-widest2 uppercase">
                  {priceLine(book)}
                </p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1.0, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7"
            >
              {book.anchor && (
                <div>
                  <ScripturalAnchor anchor={book.anchor} />
                </div>
              )}

              {book.description && (
                <p className="font-serif text-ink-soft text-lg md:text-xl leading-relaxed mt-7 max-w-prose">
                  {book.description}
                </p>
              )}

              {/* Hero already surfaces every digital storefront — only print
                  editions live here, since they're a different physical SKU. */}
              {secondary.length > 0 && (
                <div className="mt-10">
                  <div className="eyebrow mb-3">Print editions</div>
                  <div className="flex flex-wrap gap-2.5">
                    {secondary.map((s) => (
                      <StorefrontButton
                        key={s.id}
                        {...s}
                        titleId={book.slug}
                        slug={book.slug}
                        campaign={`books-${book.slug}`}
                      />
                    ))}
                  </div>
                </div>
              )}
              {/* Reserved direct-buy slot (Stripe / Shopify) — hidden until enabled. */}
              <BuyDirectSlot enabled={false} />
            </motion.div>
          </div>
        </section>

        {/* === Email capture + cross-sell === */}
        <section className="bg-cream section-y">
          <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <div className="eyebrow mb-4">Stay in touch</div>
                <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight tracking-tightish max-w-md">
                  Be told when the next volume is ready.
                </h2>
                <div className="mt-6">
                  <EmailCapture
                    titleId={book.slug}
                    slug={book.slug}
                    prompt="One email when the next book ships. Nothing else."
                  />
                </div>
              </div>

              {crossSellBooks.length > 0 && (
                <div className="lg:col-span-7">
                  <div className="eyebrow mb-4">Continue with</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
                    {crossSellBooks.map((b, i) => (
                      <div
                        key={b.slug}
                        onClick={() =>
                          trackEvent("cross_sell_click", {
                            title_id: book.slug,
                            slug: book.slug,
                            target_slug: b.slug,
                          })
                        }
                      >
                        <BookCard book={b} index={i} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </PageShell>
  );
}
